import React from "react"
import { useCallback, useEffect, useState } from "react"
import { useGenerateWithAIAction } from "@/shared/hooks/useGenerateWithAIAction"
import { Notify, SelectedImage, toBulkSections } from "@/features/bulkedit/types"

interface UseBulkEditArgs {
  userId: string | null
  maxImages: number
  notify: Notify
}

interface UseBulkEditResult {
  selectedPromptId: string | null
  selectedImages: SelectedImage[]
  imageToDeleteId: string | null
  generating: boolean
  setSelectedPromptId: (promptId: string | null) => void
  setImageToDeleteId: (imageId: string | null) => void
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  handleConfirmDeleteImage: () => void
  handleGenerateWithAI: () => Promise<void>
}

const useBulkEdit = ({ userId, maxImages, notify }: UseBulkEditArgs): UseBulkEditResult => {
  const [selectedPromptId, setSelectedPromptId] = useState<string | null>(null)
  const [selectedImages, setSelectedImages] = useState<SelectedImage[]>([])
  const [imageToDeleteId, setImageToDeleteId] = useState<string | null>(null)
  const { loading: generating, run } = useGenerateWithAIAction({
    userId,
    notify,
    sectionsFactory: () => {
      if (!selectedPromptId) {
        return []
      }
      return toBulkSections(selectedImages, selectedPromptId)
    },
    successMessage: "Bulk AI generation started successfully.",
    failureMessage: "Failed to start bulk generation. Please try again."
  })

  useEffect(() => {
    return () => {
      selectedImages.forEach(image => URL.revokeObjectURL(image.previewUrl))
    }
  }, [selectedImages])

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files ?? [])
      event.target.value = ""

      if (files.length === 0) {
        return
      }

      const limitedFiles = files.slice(0, maxImages)
      if (files.length > maxImages) {
        notify(`Only the first ${maxImages} images were selected.`, "warning")
      }

      setSelectedImages(previous => {
        previous.forEach(image => URL.revokeObjectURL(image.previewUrl))
        return limitedFiles.map((file, index) => ({
          id: `${file.name}-${file.size}-${index}`,
          file,
          previewUrl: URL.createObjectURL(file)
        }))
      })
    },
    [maxImages, notify]
  )

  const handleConfirmDeleteImage = useCallback(() => {
    if (!imageToDeleteId) {
      return
    }

    setSelectedImages(previous => {
      const imageToDelete = previous.find(image => image.id === imageToDeleteId)
      if (imageToDelete) {
        URL.revokeObjectURL(imageToDelete.previewUrl)
      }
      return previous.filter(image => image.id !== imageToDeleteId)
    })
    setImageToDeleteId(null)
  }, [imageToDeleteId])

  const handleGenerateWithAI = useCallback(async () => {
    if (!selectedPromptId || selectedImages.length === 0) {
      return
    }

    const outcome = await run()
    if (outcome.status === "success") {
      setSelectedImages(previous => {
        previous.forEach(image => URL.revokeObjectURL(image.previewUrl))
        return []
      })
      setSelectedPromptId(null)
    }
  }, [run, selectedImages, selectedPromptId])

  return {
    selectedPromptId,
    selectedImages,
    imageToDeleteId,
    generating,
    setSelectedPromptId,
    setImageToDeleteId,
    handleFileChange,
    handleConfirmDeleteImage,
    handleGenerateWithAI
  }
}

export { useBulkEdit }
export type { UseBulkEditArgs, UseBulkEditResult }
